import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { formatEUR } from '../utils/format';

const MyInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/my-invoices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      paid: { label: 'Bezahlt', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      overdue: { label: 'Überfällig', color: 'bg-red-100 text-red-800', icon: AlertCircle },
      cancelled: { label: 'Storniert', color: 'bg-gray-100 text-gray-800', icon: AlertCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const InvoiceDetailModal = ({ invoice, onClose }) => {
    if (!invoice) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Rechnung #{invoice.invoice_number}</h2>
                <p className="text-sm text-gray-600">
                  Bestellung: #{invoice.order_number}
                </p>
              </div>
              <Button variant="ghost" onClick={onClose}>✕</Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Status */}
            <div>
              <h3 className="font-semibold mb-2">Zahlungsstatus</h3>
              {getStatusBadge(invoice.payment_status)}
              {invoice.paid_at && (
                <p className="text-sm text-gray-600 mt-2">
                  Bezahlt am: {new Date(invoice.paid_at).toLocaleDateString('de-DE')}
                </p>
              )}
            </div>

            {/* Items */}
            <div>
              <h3 className="font-semibold mb-3">Positionen</h3>
              <div className="space-y-3">
                {invoice.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-gray-600">Menge: {item.quantity}</p>
                    </div>
                    <p className="font-semibold">{formatEUR((item.price * item.quantity))}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Billing Address */}
            <div>
              <h3 className="font-semibold mb-2">Rechnungsadresse</h3>
              <div className="p-3 bg-gray-50 rounded text-sm">
                <p>{invoice.billing_address.name}</p>
                <p>{invoice.billing_address.street}</p>
                <p>{invoice.billing_address.zip} {invoice.billing_address.city}</p>
              </div>
            </div>

            {/* Price Summary */}
            <div className="border-t pt-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Zwischensumme:</span>
                  <span>{formatEUR(invoice.subtotal)}</span>
                </div>
                {invoice.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Rabatt:</span>
                    <span>-{formatEUR(invoice.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Versand:</span>
                  <span>{formatEUR(invoice.shipping_cost)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Gesamt:</span>
                  <span>{formatEUR(invoice.total)}</span>
                </div>
              </div>
            </div>

            {/* Download Button */}
            <div className="flex gap-3">
              <Button className="flex-1" disabled>
                <Download className="h-4 w-4 mr-2" />
                PDF herunterladen (Bald verfügbar)
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">Lade Rechnungen...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Meine Rechnungen</h1>
            <p className="text-gray-600">Übersicht über alle Ihre Rechnungen</p>
          </div>

          {invoices.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">Noch keine Rechnungen</p>
                <p className="text-sm">Rechnungen werden nach Bestellungen erstellt</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <Card key={invoice.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">#{invoice.invoice_number}</h3>
                          {getStatusBadge(invoice.payment_status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          Bestellung: #{invoice.order_number}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(invoice.created_at).toLocaleDateString('de-DE')} • 
                          Betrag: <strong>{formatEUR(invoice.total)}</strong>
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setSelectedInvoice(invoice)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Ansehen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />

      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  );
};

export default MyInvoices;
